import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { GameRequestButton } from "@/components/GameRequestButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { GenreSelector, parseGenres, formatGenres } from "@/components/ui/genre-selector";
import { GAME_GENRES } from "@/data/gameGenres";
import { Layout } from "@/components/Layout";
import { Key, ExternalLink, Play, Download, Star, Calendar, Users, MessageSquare, ArrowLeft, Check, X, Edit, Image, Upload, Trash2, ChevronLeft, ChevronRight, AlertTriangle, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";
import { getDeveloperProfileUrl, getDeveloperDisplayName } from "@/lib/developerUtils";
import { StudioName } from "@/components/StudioName";
import { GameCoinsDisplay } from "@/components/GamePointsDisplay";
import { SteamProfileBlock } from "@/components/SteamProfileBlock";
import { GameMissionTracker } from "@/components/GameMissionTracker";
import { GameLikeButton } from "@/components/GameLikeButton";
import { GameFavoriteButton } from "@/components/GameFavoriteButton";
import { DeveloperLikeButton } from "@/components/DeveloperLikeButton";
import { getGameDetails } from "@/services/steamService";
import { checkPressKitExists } from "@/services/pressKitService";

interface Game {
  id: string;
  title: string;
  description: string;
  genres: string[];
  cover_image_url: string;
  screenshots: string[];
  steam_url: string;
  steam_game_id?: string | number;
  price: number;
  available_keys: number;
  available_public_keys?: number;
  available_creator_keys?: number;
  total_keys: number;
  created_at: string;
  release_date?: string;
  highlight_id: number;
  highlight_end_date?: string;
  is_published: boolean;
  allow_requests: boolean;
  developer_id: string;
  youtube_trailer_url?: string;
  developer?: {
    display_name: string;
    studio_name?: string;
    studio_logo_url?: string;
    studio_slug?: string;
    is_premium?: boolean;
    premium_end_date?: string | null;
  };
}

interface KeyRequest {
  id: string;
  game_id: string;
  creator_id: string;
  created_at: string;
  message: string;
  status: string;
  response_message?: string;
  creator: {
    id: string;
    display_name: string;
    avatar_url?: string;
    bio?: string;
    platform?: string;
    channel_url?: string;
    subscriber_count?: number;
    favorite_genres?: string;
    twitch_url?: string;
    youtube_url?: string;
    tiktok_url?: string;
    instagram_url?: string;
    twitter_url?: string;
    bluesky_url?: string;
    steam_url?: string;
    steam_id?: string;
    is_verified?: boolean | null;
    // Twitch stats
    twitch_followers_count?: number;
    twitch_subscribers_count?: number;
    twitch_views_count?: number;
    // YouTube stats
    youtube_subscribers_count?: number;
    youtube_views_count?: number;
    has_reported?: boolean;
    report_details?: {
      total_reports: number;
      latest_report_date: string | null;
    };
    trust_score?: number;
  };
}

export default function GameDetail() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user, userType } = useAuth();
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedScreenshot, setSelectedScreenshot] = useState<string | null>(null);
  const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(0);
  const [isRequestingKey, setIsRequestingKey] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [gameRequests, setGameRequests] = useState<KeyRequest[]>([]);
  const [isDeletingGame, setIsDeletingGame] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditingGame, setIsEditingGame] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingSteamData, setIsLoadingSteamData] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);
  const [hasPressKit, setHasPressKit] = useState(false);

  // States for existing images
  const [existingCoverImage, setExistingCoverImage] = useState<string>("");
  const [existingScreenshots, setExistingScreenshots] = useState<string[]>([]);
  const [deletedScreenshots, setDeletedScreenshots] = useState<string[]>([]);
  const [coverImageDeleted, setCoverImageDeleted] = useState(false);

  // Form states for editing
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    genres: [] as string[],
    steam_url: "",
    steam_game_id: "",
    price: 0,
    release_date: "",
    highlight_id: 1,
    highlight_end_date: "",
    youtube_trailer_url: ""
  });

  useEffect(() => {
    if (gameId && user?.id) {
      fetchGame();
    }
  }, [gameId, user?.id]);

  useEffect(() => {
    if (gameId) {
      checkPressKitExists(gameId).then(setHasPressKit);
    }
  }, [gameId]);

  // Track game page visit for missions
  useEffect(() => {
    if (game && user?.id) {
      // This will be handled by GameMissionTracker component
    }
  }, [game, user?.id]);

  const fetchGame = async () => {
    try {
      // Attendre que l'utilisateur soit chargé
      if (!user) {
        return;
      }

      const { data, error } = await supabase
        .from("games")
        .select(`
          *,
          developer:profiles!games_developer_id_fkey(
            display_name,
            studio_name,
            studio_logo_url,
            studio_slug,
            is_premium,
            premium_end_date
          )
        `)
        .eq("id", gameId)
        .single();

      if (error) {
        console.error('Error fetching game:', error);
        toast.error("Game not found");
        navigate(-1);
        return;
      }

      // Compter les clés publiques et créateurs disponibles
      let availablePublicKeys = 0;
      let availableCreatorKeys = 0;

      if (data && (data as any).id) {
        const gameId = (data as any).id;

        // Compter les clés publiques disponibles (public = true)
        const { count: publicCount, error: publicError } = await (supabase as any)
          .from("keys")
          .select("*", { count: 'exact', head: true })
          .eq("game_id", gameId)
          .eq("is_claimed", false)
          .eq("public", true);

        if (publicError) {
          console.error('Error counting public keys:', publicError);
        }

        // Compter les clés créateurs disponibles (public = false ou NULL)
        const { count: creatorCount, error: creatorError } = await (supabase as any)
          .from("keys")
          .select("*", { count: 'exact', head: true })
          .eq("game_id", gameId)
          .eq("is_claimed", false)
          .or("public.is.null,public.eq.false");

        if (creatorError) {
          console.error('Error counting creator keys:', creatorError);
        }

        availablePublicKeys = publicCount || 0;
        availableCreatorKeys = creatorCount || 0;
      }

      setGame({
        ...data,
        genres: parseGenres(data.genre || ""),
        highlight_id: (data as any).highlight_id || 1,
        highlight_end_date: (data as any).highlight_end_date || "",
        is_published: (data as any).is_published || false,
        allow_requests: (data as any).allow_requests !== false,
        price: (data as any).price || 0,
        available_public_keys: availablePublicKeys,
        available_creator_keys: availableCreatorKeys
      });

      // Initialiser le formulaire d'édition
      setEditForm({
        title: data.title,
        description: data.description,
        genres: parseGenres(data.genre || ""),
        steam_url: data.steam_url || "",
        steam_game_id: (data as any).steam_game_id ? String((data as any).steam_game_id) : "",
        price: (data as any).price || 0,
        release_date: (data as any).release_date || data.created_at || "",
        highlight_id: (data as any).highlight_id || 1,
        highlight_end_date: (data as any).highlight_end_date || "",
        youtube_trailer_url: (data as any).youtube_trailer_url || ""
      });

      // Initialiser les images existantes
      setExistingCoverImage(data.cover_image_url || "");
      setExistingScreenshots(data.screenshots || []);
      setDeletedScreenshots([]);
      setCoverImageDeleted(false);

      // Récupérer les demandes de clés pour les développeurs
      if (user.id === data.developer_id) {
        await fetchGameRequests(data.id);
      }
    } catch (error) {
      console.error('Error fetching game:', error);
      toast.error("Error loading game");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchGameRequests = async (gameId: string) => {
    try {
      // Only fetch basic request info for counting pending requests
      const { data } = await supabase
        .from("key_requests")
        .select("id, status")
        .eq("game_id", gameId);

      const requests = (data as any[]) || [];
      setGameRequests(requests as KeyRequest[]);
    } catch (error) {
      console.error('Error fetching game requests:', error);
    }
  };

  const handleOpenRequestDialog = (game?: Game) => {
    if (!game) return;
    setIsRequestingKey(true);
  };

  const handleRequestKey = async (game?: Game) => {
    if (!user || !game) return;

    setIsSubmittingRequest(true);
    try {
      const { error } = await supabase
        .from("key_requests")
        .insert({
          game_id: game.id,
          creator_id: user.id,
          developer_id: game.developer_id || '',
          message: requestMessage.trim() || null
        });

      if (error) {
        if (error.code === "23505") {
          toast.error("You've already requested a key for this game");
        } else {
          toast.error(error.message);
        }
      } else {
        toast.success("Key request submitted successfully!");
        setIsRequestingKey(false);
        setRequestMessage("");
        if (game?.id) {
          fetchGameRequests(game.id);
        }
      }
    } catch (error) {
      console.error('Error requesting key:', error);
      toast.error("Error submitting key request");
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleViewRequests = () => {
    navigate(`/key-requests?game=${game?.id}`);
  };

  const handleTogglePublish = async () => {
    try {
      const { error } = await supabase
        .from('games')
        .update({ is_published: !game.is_published } as any)
        .eq('id', game.id);

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(`Game ${!game.is_published ? 'published' : 'unpublished'} successfully!`);
        fetchGame(); // Refresh the game data
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      toast.error('Error updating publish status');
    }
  };

  const handleToggleRequests = async () => {
    try {
      const { error } = await supabase
        .from('games')
        .update({ allow_requests: !game.allow_requests } as any)
        .eq('id', game.id);

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(`Requests ${!game.allow_requests ? 'enabled' : 'disabled'} successfully!`);
        fetchGame(); // Refresh the game data
      }
    } catch (error) {
      console.error('Error toggling requests status:', error);
      toast.error('Error updating requests status');
    }
  };

  // Function to map Steam genres to application genres
  const mapSteamGenresToAppGenres = (steamGenres: any[]): string[] => {
    if (!steamGenres || !Array.isArray(steamGenres)) return [];

    const genreMap: { [key: string]: string | string[] } = {
      // Direct matches
      "Action": "Action",
      "Free To Play": "Free-to-Play",
      "Adventure": "Adventure",
      "RPG": "RPG",
      "Strategy": "Real-Time Strategy (RTS)",
      "Simulation": "Simulation",
      "Sports": "Sports",
      "Racing": "Racing",
      "Indie": "Indie",
      "Casual": "Casual",
      "Early Access": "Early Access",

      // Action subgenres
      "Action RPG": "Action RPG",
      "Hack and Slash": "Hack and Slash",
      "Beat 'em up": "Beat 'em up",
      "Fighting": "Fighting",
      "Stealth": "Stealth",
      "Survival": "Survival",
      "Survival Horror": "Survival Horror",
      "Platformer": "Platformer",

      // Shooter genres
      "FPS": "First-Person Shooter (FPS)",
      "First-Person Shooter": "First-Person Shooter (FPS)",
      "Third-Person Shooter": "Third-Person Shooter (TPS)",
      "TPS": "Third-Person Shooter (TPS)",
      "Tactical Shooter": "Tactical Shooter",
      "Battle Royale": "Battle Royale",
      "Hero Shooter": "Hero Shooter",

      // Strategy subgenres
      "Real-Time Strategy": "Real-Time Strategy (RTS)",
      "RTS": "Real-Time Strategy (RTS)",
      "Turn-Based Strategy": "Turn-Based Strategy",
      "Tower Defense": "Tower Defense",
      "4X": "4X Strategy",
      "4X Strategy": "4X Strategy",
      "Grand Strategy": "Grand Strategy",
      "City Builder": "City Builder",
      "Management": "Management",

      // Simulation subgenres
      "Life Simulation": "Life Simulation",
      "Vehicle Simulation": "Vehicle Simulation",
      "Flight Simulation": "Flight Simulation",
      "Space Simulation": "Space Simulation",
      "Farming Simulation": "Farming Simulation",
      "Business Simulation": "Business Simulation",
      "Sports Simulation": "Sports Simulation",

      // RPG subgenres
      "JRPG": "JRPG",
      "Western RPG": "Western RPG",
      "Roguelike": "Roguelike",
      "Roguelite": "Roguelite",
      "Dungeon Crawler": "Dungeon Crawler",
      "MMORPG": "MMORPG",
      "Open-World RPG": "Open-World RPG",

      // Sports subgenres
      "Football": "Football/Soccer",
      "Soccer": "Football/Soccer",
      "Basketball": "Basketball",
      "Tennis": "Tennis",
      "Golf": "Golf",
      "Baseball": "Baseball",
      "Hockey": "Hockey",
      "Wrestling": "Wrestling",
      "Boxing": "Boxing",
      "Martial Arts": "Martial Arts",
      "Extreme Sports": "Extreme Sports",

      // Puzzle & Casual
      "Puzzle": "Puzzle",
      "Match-3": "Match-3",
      "Hidden Object": "Hidden Object",
      "Point & Click": "Point & Click",
      "Visual Novel": "Visual Novel",
      "Interactive Fiction": "Interactive Fiction",
      "Educational": "Educational",
      "Trivia": "Trivia",
      "Card Game": "Card Game",
      "Board Game": "Board Game",
      "Party Game": "Party Game",

      // Sandbox & Creation
      "Sandbox": "Sandbox",
      "Open World": "Open World",
      "Crafting": "Crafting",
      "Building": "Building",
      "Creative": "Creative",
      "Art Game": "Art Game",

      // Multiplayer
      "Multiplayer": "Multiplayer",
      "Co-op": "Co-op",
      "Cooperative": "Co-op",
      "Competitive": "Competitive",
      "Team-Based": "Team-Based",
      "Massively Multiplayer": "Massively Multiplayer",
      "MMO": "Massively Multiplayer",
      "Local Multiplayer": "Local Multiplayer",
      "Online Multiplayer": "Online Multiplayer",

      // Indie & Niche
      "Retro": "Retro",
      "Pixel Art": "Pixel Art",
      "2D": "2D",
      "3D": "3D",
      "Side-Scroller": "Side-Scroller",
      "Top-Down": "Top-Down",
      "Isometric": "Isometric",

      // Thematic
      "Horror": "Horror",
      "Psychological Horror": "Psychological Horror",
      "Sci-Fi": "Sci-Fi",
      "Science Fiction": "Sci-Fi",
      "Fantasy": "Fantasy",
      "Medieval": "Medieval",
      "Historical": "Historical",
      "Post-Apocalyptic": "Post-Apocalyptic",
      "Cyberpunk": "Cyberpunk",
      "Steampunk": "Steampunk",
      "Western": "Western",
      "Noir": "Noir",
      "Mystery": "Mystery",
      "Thriller": "Thriller",
      "Drama": "Drama",
      "Comedy": "Comedy",
      "Romance": "Romance",

      // Special mechanics
      "Metroidvania": "Metroidvania",
      "Souls-like": "Souls-like",
      "Twin-Stick Shooter": "Twin-Stick Shooter",
      "Bullet Hell": "Bullet Hell",
      "Rhythm": "Rhythm",
      "Music": "Music",
      "Dance": "Dance",
      "Fitness": "Fitness",
      "VR": "VR",
      "Virtual Reality": "VR",
      "Augmented Reality": "Augmented Reality",

      // Platform & Style
      "Mobile": "Mobile",
      "Browser Game": "Browser Game",
      "Beta": "Beta",
      "Demo": "Demo",
      "Mod": "Mod",
      "Total Conversion": "Total Conversion",

      // Other
      "Hardcore": "Hardcore",
      "Narrative": "Narrative",
      "Story Rich": "Story Rich",
      "Character Customization": "Character Customization",
      "Character Progression": "Character Progression",
      "Skill-Based": "Skill-Based",
      "Time Management": "Time Management",
      "Resource Management": "Resource Management",
      "Trading": "Trading",
      "Economy": "Economy",
      "Diplomacy": "Diplomacy",
      "Warfare": "Warfare",
      "Exploration": "Exploration",
      "Discovery": "Discovery",
      "Collection": "Collection",
      "Achievement Hunting": "Achievement Hunting",
      "Speedrun": "Speedrun",
      "Challenge": "Challenge",
      "Relaxing": "Relaxing",
      "Meditative": "Meditative",
      "Atmospheric": "Atmospheric",
      "Immersive": "Immersive",
      "Cinematic": "Cinematic",
      "Theatrical": "Theatrical",
      "Experimental": "Experimental",
      "Abstract": "Abstract",
      "Minimalist": "Minimalist",
      "Maximalist": "Maximalist",
      "Dystopian": "Dystopian",
      "Procedural Generation": "Procedural Generation",
      "Adult": "Adult",
      "Chill": "Chill",
      "Cosy": "Cosy",
      "Cute": "Cute",
      "Pirate": "Pirate",
      "Animal": "Animal",
      "Zombie": "Zombie",
      "Alien": "Alien",
      "Vampire": "Vampire",
      "Werewolf": "Werewolf",
      "Wizard": "Wizard",
    };

    const mappedGenres: string[] = [];
    steamGenres.forEach((genre: any) => {
      const description = genre.description || "";
      if (!description) return;

      // Check direct mapping first
      const mapped = genreMap[description];
      if (mapped) {
        if (Array.isArray(mapped)) {
          mappedGenres.push(...mapped);
        } else {
          mappedGenres.push(mapped);
        }
        return;
      }

      // Try case-insensitive match
      const lowerDescription = description.toLowerCase();
      const caseInsensitiveMatch = Object.entries(genreMap).find(([key]) =>
        key.toLowerCase() === lowerDescription
      );
      if (caseInsensitiveMatch) {
        const mapped = caseInsensitiveMatch[1];
        if (Array.isArray(mapped)) {
          mappedGenres.push(...mapped);
        } else {
          mappedGenres.push(mapped);
        }
        return;
      }

      // Try partial matching with GAME_GENRES
      const matchingGenre = GAME_GENRES.find(g => {
        const lowerG = g.toLowerCase();
        const lowerD = lowerDescription;
        return lowerG === lowerD ||
          lowerG.includes(lowerD) ||
          lowerD.includes(lowerG) ||
          lowerG.replace(/[^a-z0-9]/g, '') === lowerD.replace(/[^a-z0-9]/g, '');
      });
      if (matchingGenre) {
        mappedGenres.push(matchingGenre);
        return;
      }

      // Try fuzzy matching - check if any word matches
      const descriptionWords = lowerDescription.split(/[\s-]+/);
      const fuzzyMatch = GAME_GENRES.find(g => {
        const lowerG = g.toLowerCase();
        return descriptionWords.some(word =>
          word.length > 3 && lowerG.includes(word)
        );
      });
      if (fuzzyMatch) {
        mappedGenres.push(fuzzyMatch);
      }
    });

    return [...new Set(mappedGenres)];
  };

  const handleFillWithSteam = async () => {
    if (!game) return;

    setIsLoadingSteamData(true);

    try {
      const steamGameId = (game as any).steam_game_id;

      if (!steamGameId) {
        toast.error("Steam Game ID is required. Please set it in the game edit form.");
        return;
      }

      const steamData = await getGameDetails(Number(steamGameId));

      if (!steamData) {
        toast.error("Failed to fetch game data from Steam");
        return;
      }

      // Mapper les données Steam aux champs du formulaire
      const mappedGenres = mapSteamGenresToAppGenres(steamData.genres || []);

      // Extraire le prix depuis package_groups (chercher le premier package non gratuit)
      let price = 0;
      if (steamData.is_free) {
        price = 0;
      } else if (steamData.package_groups && steamData.package_groups.length > 0) {
        const firstGroup = steamData.package_groups[0];
        if (firstGroup.subs && firstGroup.subs.length > 0) {
          // Chercher le premier package payant
          const paidPackage = firstGroup.subs.find((p: any) => !p.is_free_license && p.price_in_cents_with_discount > 0);
          if (paidPackage) {
            // Convertir les centimes en euros (ou autre devise selon le besoin)
            price = paidPackage.price_in_cents_with_discount / 100;
          }
        }
      }

      // Extraire les screenshots
      const screenshots = (steamData.screenshots || []).map((s: any) => s.path_full || s.path_thumbnail).filter(Boolean);

      // Formater la date de sortie
      let releaseDate = "";
      if (steamData.release_date && steamData.release_date.date) {
        try {
          const date = new Date(steamData.release_date.date);
          if (!isNaN(date.getTime())) {
            releaseDate = date.toISOString().split('T')[0]; // Format YYYY-MM-DD
          }
        } catch (e) {
          console.warn("Could not parse release date:", steamData.release_date.date);
        }
      }

      // Construire l'URL Steam si on a le steam_appid
      const steamUrl = steamData.steam_appid
        ? `https://store.steampowered.com/app/${steamData.steam_appid}`
        : game.steam_url || "";

      // Mettre à jour le formulaire d'édition
      setEditForm({
        title: steamData.name || editForm.title,
        description: steamData.detailed_description || steamData.about_the_game || steamData.short_description || editForm.description,
        genres: mappedGenres.length > 0 ? mappedGenres : editForm.genres,
        steam_url: steamUrl,
        steam_game_id: steamGameId.toString(),
        price: price || editForm.price,
        release_date: releaseDate || editForm.release_date,
        highlight_id: editForm.highlight_id,
        highlight_end_date: editForm.highlight_end_date
      });

      // Mettre à jour les images existantes avec les URLs Steam
      if (steamData.header_image) {
        setExistingCoverImage(steamData.header_image);
      }

      if (screenshots.length > 0) {
        setExistingScreenshots(screenshots);
      }

      // Ouvrir automatiquement le formulaire d'édition
      setIsEditingGame(true);

      toast.success("Game data filled from Steam! Please review and save.");
    } catch (error) {
      console.error('Error fetching Steam data:', error);
      toast.error("Error fetching Steam data");
    } finally {
      setIsLoadingSteamData(false);
    }
  };


  const handleDeleteGame = async () => {
    if (!game || deleteConfirmation !== game.title) {
      toast.error("Game title doesn't match. Please type the exact game title.");
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("games")
        .delete()
        .eq("id", game.id);

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Game deleted successfully!");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error('Error deleting game:', error);
      toast.error("Error deleting game");
    } finally {
      setIsDeleting(false);
      setIsDeletingGame(false);
      setDeleteConfirmation("");
    }
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setCoverImageFile(file);
    }
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not a valid image`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    setScreenshotFiles(prev => [...prev, ...validFiles]);
  };

  const removeScreenshotFile = (index: number) => {
    setScreenshotFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingScreenshot = (index: number) => {
    const screenshotToDelete = existingScreenshots[index];
    setExistingScreenshots(prev => prev.filter((_, i) => i !== index));
    setDeletedScreenshots(prev => [...prev, screenshotToDelete]);
  };

  const removeExistingCoverImage = () => {
    setExistingCoverImage("");
    setCoverImageDeleted(true);
  };

  const restoreExistingScreenshot = (screenshotUrl: string) => {
    setExistingScreenshots(prev => [...prev, screenshotUrl]);
    setDeletedScreenshots(prev => prev.filter(url => url !== screenshotUrl));
  };

  const restoreExistingCoverImage = () => {
    setExistingCoverImage(game?.cover_image_url || "");
    setCoverImageDeleted(false);
  };

  const uploadImage = async (file: File, path: string): Promise<string> => {
    const { error: uploadError } = await supabase.storage
      .from('game-images')
      .upload(path, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('game-images')
      .getPublicUrl(path);

    return data.publicUrl;
  };

  const handleEditGame = async () => {
    if (!game) return;

    setIsUploading(true);
    try {
      let coverImageUrl = existingCoverImage;
      let screenshotUrls = existingScreenshots;

      // Upload cover image if changed
      if (coverImageFile) {
        const coverPath = `covers/${game.id}/${Date.now()}-${coverImageFile.name}`;
        coverImageUrl = await uploadImage(coverImageFile, coverPath);
      } else if (coverImageDeleted) {
        coverImageUrl = null;
      }

      // Upload new screenshots
      if (screenshotFiles.length > 0) {
        const newScreenshotUrls = await Promise.all(
          screenshotFiles.map(async (file, index) => {
            const screenshotPath = `screenshots/${game.id}/${Date.now()}-${index}-${file.name}`;
            return await uploadImage(file, screenshotPath);
          })
        );
        screenshotUrls = [...screenshotUrls, ...newScreenshotUrls];
      }

      // Update game in database
      const { error } = await supabase
        .from("games")
        .update({
          title: editForm.title.trim(),
          description: editForm.description.trim(),
          genre: formatGenres(editForm.genres),
          cover_image_url: coverImageUrl,
          screenshots: screenshotUrls,
          steam_url: editForm.steam_url.trim() || null,
          steam_game_id: editForm.steam_game_id ? parseInt(editForm.steam_game_id) : null,
          price: editForm.price,
          release_date: editForm.release_date,
          highlight_id: editForm.highlight_id,
          highlight_end_date: editForm.highlight_end_date || null,
          youtube_trailer_url: editForm.youtube_trailer_url || null,
        })
        .eq("id", game.id);

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Game updated successfully!");
        setIsEditingGame(false);
        // Reset form states
        setCoverImageFile(null);
        setScreenshotFiles([]);
        setDeletedScreenshots([]);
        setCoverImageDeleted(false);
        // Rafraîchir les données du jeu
        await fetchGame();
      }
    } catch (error) {
      console.error('Error updating game:', error);
      toast.error("Error updating game");
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url: string): string => {
    if (!url) return '';

    // Handle different YouTube URL formats
    // https://www.youtube.com/watch?v=VIDEO_ID
    // https://youtu.be/VIDEO_ID
    // https://www.youtube.com/embed/VIDEO_ID

    const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (videoIdMatch && videoIdMatch[1]) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }

    // If already an embed URL, return as is
    if (url.includes('youtube.com/embed/')) {
      return url;
    }

    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading game details...</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Game not found</h1>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      {game && (
        <SEO
          title={game.title}
          description={game.description}
          image={game.cover_image_url}
        />
      )}
      {/* Track game page visit for missions */}
      {game && user && <GameMissionTracker gameId={game.id} gameTitle={game.title} />}

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: game.cover_image_url ? `url(${game.cover_image_url})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background"></div>
        </div>

        <div className="container relative z-10 px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-2">
            <span className="gradient-text">{game.title}</span>
          </h1>
          {/* Tags Section */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 justify-center">
              {game.genres.map((genre, index) => (
                <Badge key={index} variant="outline" className="text-xs bg-border">
                  {genre}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-20">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="bg-black/50 hover:bg-black/70 text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {userType === 'developer' && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingGame(true)}
                className="bg-black/50 hover:bg-black/70 text-white"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Game
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTogglePublish}
                className={game.is_published ? "bg-green-600/50 hover:bg-green-600/70 text-white" : "bg-red-600/50 hover:bg-red-600/70 text-white"}
              >
                <Star className="w-4 h-4 mr-2" />
                {game.is_published ? "Published" : "Publish"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleRequests}
                className={game.allow_requests ? "bg-green-600/50 hover:bg-green-600/70 text-white" : "bg-red-600/50 hover:bg-red-600/70 text-white"}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {game.allow_requests ? "Requests Open" : "Allow Requests"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFillWithSteam}
                disabled={isLoadingSteamData}
                className="bg-blue-600/50 hover:bg-blue-600/70 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingSteamData ? 'animate-spin' : ''}`} />
                {isLoadingSteamData ? "Loading datas..." : "Fill with Steam"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsDeletingGame(true)}
                className="bg-red-600/50 hover:bg-red-600/70 text-white"
              >
                <X className="w-4 h-4 mr-2" />
                Delete Game
              </Button>
            </div>
          )}
        </div>
      </section>

      <div className="container mx-auto px-4 py-8 -mt-[15vh] relative z-10">

        {/* Keys Banner */}
        <Card className="mb-8 p-4 md:p-6 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Left side - Release Date */}
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Released</span>
              <span className="font-medium">{formatDate(game.release_date || game.created_at)}</span>
            </div>

            {/* Right side - Keys count and buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {userType === 'developer' && (
                <div className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-primary" />
                  <span className="text-lg font-bold text-primary">
                    {game.available_keys}
                    <span className="text-sm text-muted-foreground"> / {game.total_keys}</span>
                  </span>
                </div>
              )}

              {/* Request key functionality for creators */}
              {userType === 'creator' && (
                <div className="flex gap-2">
                  <GameRequestButton
                    gameId={game.id}
                    gameTitle={game.title}
                    allowRequests={game.allow_requests}
                    availableKeys={game.available_creator_keys || game.available_keys || 0}
                    onRequestClick={() => handleOpenRequestDialog(game)}
                  />
                </div>
              )}

              {/* Like and favorite buttons for players (no request button - players buy with coins only) */}
              {userType === 'player' && (
                <div className="flex gap-2 flex-col sm:flex-row">
                  <GameLikeButton gameId={game.id} />
                  <GameFavoriteButton gameId={game.id} />
                </div>
              )}

              {/* Coins cost display for players */}
              {userType === 'player' && game && (
                <GameCoinsDisplay
                  gameId={game.id}
                  gameTitle={game.title}
                  price={game.price}
                  availableKeys={game.available_public_keys || 0}
                  onPurchaseSuccess={() => {
                    // Refresh game data to update available keys
                    fetchGame();
                  }}
                />
              )}

              {userType === 'developer' && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleViewRequests}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  <MessageSquare className="w-5 h-5" />
                  {gameRequests.filter(req => req.status === 'pending').length} Requests
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Screenshots Gallery */}
        {game.screenshots && game.screenshots.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Screenshots</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {game.screenshots.slice(0, 4).map((screenshot, index) => (
                <Card
                  key={index}
                  className="overflow-hidden cursor-pointer hover:scale-105 transition-transform relative rounded-custom-paddings p-0"
                  onClick={() => {
                    setSelectedScreenshot(screenshot);
                    setCurrentScreenshotIndex(index);
                  }}
                >
                  <img
                    src={screenshot}
                    alt={`${game.title} screenshot ${index + 1}`}
                    className="w-full h-48 object-cover"
                  />
                  {/* Badge overlay sur le dernier screenshot visible */}
                  {index === 3 && game.screenshots.length > 4 && (
                    <div className="absolute bottom-2 right-8 bg-black/70 text-white px-2 py-1 rounded-md text-xs font-medium">
                      +{game.screenshots.length - 4} Screenshots
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Description and Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold mb-4">About This Game</h2>

            {/* YouTube Trailer */}
            {game.youtube_trailer_url && (
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-4">Trailer</h3>
                <div className="aspect-video w-full rounded-lg overflow-hidden">
                  <iframe
                    src={getYouTubeEmbedUrl(game.youtube_trailer_url)}
                    title={`${game.title} Trailer`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            <div className="prose prose-invert max-w-none">
              <div
                className="text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: game.description }}
              />
            </div>
          </div>

          <div className="space-y-6">
            {/* Game Links */}
            {/* <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Get This Game</h3>
              <div className="space-y-3">
                {game.steam_url && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    asChild
                  >
                    <a href={game.steam_url} target="_blank" rel="noopener noreferrer">
                      <Play className="w-4 h-4 mr-2" />
                      View on Steam
                    </a>
                  </Button>
                )}
              </div>
            </Card> */}


            {/* Game Stats */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Game Informations</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Developer</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(getDeveloperProfileUrl({ id: game.developer_id, studio_slug: game.developer?.studio_slug }))}
                      className="font-medium text-primary hover:underline transition-colors"
                    >
                      {game.developer ? (
                        <StudioName
                          studioName={game.developer.studio_name}
                          displayName={game.developer.display_name}
                          isPremium={game.developer.is_premium}
                          premiumEndDate={game.developer.premium_end_date}
                        />
                      ) : 'Unknown Developer'}
                    </button>
                    {userType === 'player' && (
                      <DeveloperLikeButton developerId={game.developer_id} className="ml-2" />
                    )}
                  </div>
                </div>
                {userType === 'developer' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Available Keys</span>
                      <span className="font-medium">{game.available_keys}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Keys</span>
                      <span className="font-medium">{game.total_keys}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Release Date</span>
                  <span className="font-medium">{formatDate(game.release_date || game.created_at)}</span>
                </div>

                {hasPressKit && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    asChild
                  >
                    <a href={`/presskit/${gameId}`} target="_blank" rel="noopener noreferrer">
                      <FileText className="w-4 h-4 mr-2" />
                      Press Kit
                    </a>
                  </Button>
                )}
              </div>
            </Card>

            {/* Steam Widget */}
            {game.steam_game_id && (
              <Card className="p-0 mt-6 bg-transparent border-[#1b2838] text-[#c7d5e0]">
                <div className="w-full">
                  <iframe
                    src={`https://store.steampowered.com/widget/${game.steam_game_id}/?utm_source=indiekeys&utm_campaign=indiekeys`}
                    frameBorder="0"
                    width="100%"
                    height="190"
                    style={{ border: 'none', overflow: 'hidden' }}
                  ></iframe>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Screenshot Modal */}
      <Dialog open={!!selectedScreenshot} onOpenChange={() => setSelectedScreenshot(null)}>
        <DialogContent className="max-w-[95vw] md:max-w-[80vw] max-h-[90vh] md:max-h-[80vh] p-0 rounded-custom-paddings">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>
              {game.title} - Screenshot {currentScreenshotIndex + 1} of {game.screenshots?.length || 0}
            </DialogTitle>
          </DialogHeader>
          {selectedScreenshot && game.screenshots && (
            <div className="relative flex items-center justify-center p-0 sm:p-2 min-h-0">
              <img
                src={selectedScreenshot}
                alt={`${game.title} screenshot ${currentScreenshotIndex + 1}`}
                className="max-w-full max-h-[calc(80vh-120px)] object-contain rounded-custom-paddings"
              />

              {/* Navigation arrows */}
              {game.screenshots.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white border-white/20"
                    onClick={() => {
                      const prevIndex = currentScreenshotIndex > 0 ? currentScreenshotIndex - 1 : game.screenshots.length - 1;
                      setCurrentScreenshotIndex(prevIndex);
                      setSelectedScreenshot(game.screenshots[prevIndex]);
                    }}
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </Button>

                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white border-white/20"
                    onClick={() => {
                      const nextIndex = currentScreenshotIndex < game.screenshots.length - 1 ? currentScreenshotIndex + 1 : 0;
                      setCurrentScreenshotIndex(nextIndex);
                      setSelectedScreenshot(game.screenshots[nextIndex]);
                    }}
                  >
                    <ChevronRight className="w-6 h-6" />
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Edit Game Dialog */}
      <Dialog open={isEditingGame} onOpenChange={setIsEditingGame}>
        <DialogContent className="bg-card max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Game</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleEditGame(); }} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Game Title</Label>
                <Input
                  id="edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-genres">Genres</Label>
                  {editForm.genres.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, genres: [] })}
                      className="text-sm text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="mt-2">
                  <GenreSelector
                    value={editForm.genres}
                    onChange={(genres) => setEditForm({ ...editForm, genres })}
                    placeholder="Select genres..."
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <RichTextEditor
                content={editForm.description}
                onChange={(content) => setEditForm({ ...editForm, description: content })}
                placeholder="Describe your game with rich text formatting..."
              />
            </div>
            <div>
              <Label htmlFor="edit-cover">
                Cover Image <span className="text-muted-foreground text-sm font-normal">(Recommendations: 1920x550 & Centered Logo)</span>
              </Label>

              {/* Existing cover image */}
              {existingCoverImage && !coverImageDeleted && (
                <div className="mt-2 space-y-2">
                  <div className="relative group">
                    <img
                      src={existingCoverImage}
                      alt="Current cover"
                      className="w-full h-32 object-cover rounded-custom-paddings"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={removeExistingCoverImage}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Current cover image</p>
                </div>
              )}

              {/* Deleted cover image (can be restored) */}
              {coverImageDeleted && (
                <div className="mt-2 p-3 bg-destructive/10 border border-destructive/20 rounded-custom-paddings">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <X className="w-4 h-4 text-destructive" />
                      <span className="text-sm text-destructive">Cover image removed</span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={restoreExistingCoverImage}
                    >
                      Restore
                    </Button>
                  </div>
                </div>
              )}

              {/* Upload new cover image */}
              <Input
                id="edit-cover"
                type="file"
                accept="image/*"
                onChange={handleCoverImageChange}
                className="cursor-pointer mt-2"
              />
              {coverImageFile && (
                <div className="mt-2 flex items-center gap-2">
                  <Image className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">{coverImageFile.name}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCoverImageFile(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="edit-screenshots">Screenshots</Label>

              {/* Existing screenshots */}
              {existingScreenshots.length > 0 && (
                <div className="mt-2 space-y-2">
                  <p className="text-sm font-medium">Current screenshots:</p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                    {existingScreenshots.map((screenshot, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={screenshot}
                          alt={`Screenshot ${index + 1}`}
                          className="w-full h-40 object-cover rounded-custom-paddings cursor-pointer"
                          onClick={() => setSelectedScreenshot(screenshot)}
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeExistingScreenshot(index)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deleted screenshots (can be restored) */}
              {deletedScreenshots.length > 0 && (
                <div className="mt-2 space-y-2">
                  <p className="text-sm font-medium text-destructive">Removed screenshots:</p>
                  <div className="space-y-1">
                    {deletedScreenshots.map((screenshot, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-destructive/10 border border-destructive/20 rounded">
                        <div className="flex items-center gap-2">
                          <X className="w-4 h-4 text-destructive" />
                          <span className="text-sm text-destructive">Screenshot {index + 1}</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => restoreExistingScreenshot(screenshot)}
                        >
                          Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload new screenshots */}
              <Input
                id="edit-screenshots"
                type="file"
                accept="image/*"
                multiple
                onChange={handleScreenshotChange}
                className="cursor-pointer mt-2"
              />
              {screenshotFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium text-green-600">New screenshots to add:</p>
                  {screenshotFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Image className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-600">{file.name}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeScreenshotFile(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-steam">Steam URL *</Label>
                <Input
                  id="edit-steam"
                  type="url"
                  value={editForm.steam_url}
                  onChange={(e) => setEditForm({ ...editForm, steam_url: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-steam_game_id">Steam Game ID *</Label>
                <Input
                  id="edit-steam_game_id"
                  type="number"
                  value={editForm.steam_game_id}
                  onChange={(e) => setEditForm({ ...editForm, steam_game_id: e.target.value })}
                  placeholder="e.g. 2448830"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Steam App ID (for playtime tracking)
                </p>
              </div>
              <div>
                <Label htmlFor="edit-price">Price (€)</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Game price in euros (used to calculate coin cost)
                </p>
              </div>
              <div>
                <Label htmlFor="edit-youtube_trailer_url">YouTube Trailer URL</Label>
                <Input
                  id="edit-youtube_trailer_url"
                  type="url"
                  value={editForm.youtube_trailer_url || ''}
                  onChange={(e) => setEditForm({ ...editForm, youtube_trailer_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  YouTube video URL for the game trailer
                </p>
              </div>
            </div>
            <div>
              <Label htmlFor="edit-release-date">Release Date</Label>
              <Input
                id="edit-release-date"
                type="date"
                value={editForm.release_date}
                onChange={(e) => setEditForm({ ...editForm, release_date: e.target.value })}
                required
              />
            </div>
            {/* <div>
              <Label htmlFor="edit-highlight-id">Highlight Level</Label>
              <select
                id="edit-highlight-id"
                value={editForm.highlight_id}
                onChange={(e) => setEditForm({ ...editForm, highlight_id: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value={1}>Default (1)</option>
                <option value={2}>Featured (2)</option>
                <option value={3}>Premium (3)</option>
                <option value={4}>VIP (4)</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Defines how prominently your game is displayed to creators
              </p>
            </div> */}
            {/* <div>
              <Label htmlFor="edit-highlight-end-date">Highlight End Date (Optional)</Label>
              <Input
                id="edit-highlight-end-date"
                type="date"
                value={editForm.highlight_end_date}
                onChange={(e) => setEditForm({ ...editForm, highlight_end_date: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty for permanent highlight. Set a date to automatically end the highlight period.
              </p>
            </div> */}
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={isUploading}>
                {isUploading ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Game"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditingGame(false)}
                disabled={isUploading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Request Key Dialog for Creators */}
      <Dialog open={isRequestingKey} onOpenChange={setIsRequestingKey}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Key for {game?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="request-message">Message (optional)</Label>
              <Textarea
                id="request-message"
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Tell the developer why you'd like a key for this game..."
                rows={4}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleRequestKey(game)}
                disabled={isSubmittingRequest}
                className="flex-1"
              >
                {isSubmittingRequest ? "Submitting..." : "Submit Request"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsRequestingKey(false);
                  setRequestMessage("");
                }}
                disabled={isSubmittingRequest}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Game Confirmation Dialog */}
      <Dialog open={isDeletingGame} onOpenChange={setIsDeletingGame}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-custom-paddings">
              <p className="text-sm text-destructive font-medium">
                ⚠️ This action cannot be undone. This will permanently delete the game and all associated data.
              </p>
            </div>

            <div>
              <Label htmlFor="delete-confirmation">
                Type the game title to confirm deletion:
              </Label>
              <div className="mt-2">
                <p className="text-sm text-muted-foreground mb-2">
                  Game title: <span className="font-medium">{game?.title}</span>
                </p>
                <input
                  id="delete-confirmation"
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Enter game title here..."
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={handleDeleteGame}
                disabled={isDeleting || deleteConfirmation !== game?.title}
                className="flex-1"
              >
                {isDeleting ? "Deleting..." : "Delete Game"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeletingGame(false);
                  setDeleteConfirmation("");
                }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
